import React, { useState, useEffect, useCallback } from "react";
import Dialog from "@material-ui/core/Dialog";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/SearchRounded";
import { makeStyles } from "@material-ui/core/styles";

import api from "../../services/api";
import brdidLogo from "../../assets/brdid-logo.png";

const FONT = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

// dígitos nacionais → (11) 99999-9999
const fmt = d => {
  const s = String(d || "").replace(/\D/g, "");
  if (s.length === 11) return `(${s.slice(0, 2)}) ${s.slice(2, 7)}-${s.slice(7)}`;
  if (s.length === 10) return `(${s.slice(0, 2)}) ${s.slice(2, 6)}-${s.slice(6)}`;
  return s;
};

const useStyles = makeStyles(theme => ({
  paper: { width: 520, maxWidth: "94vw", borderRadius: 14, fontFamily: FONT },
  head: { padding: "18px 20px 10px" },
  titleRow: { display: "flex", alignItems: "center", gap: 10 },
  brdLogo: { height: 22, width: "auto", display: "block" },
  title: { fontSize: 18, fontWeight: 600, color: theme.app.ink },
  sub: { fontSize: 13, color: theme.app.textSecondary, marginTop: 6 },
  body: { padding: "0 20px", maxHeight: "52vh", overflowY: "auto", ...theme.scrollbarStyles },
  search: { width: "100%", marginBottom: 8 },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 6px",
    borderRadius: 8,
    cursor: "pointer",
    "&:hover": { background: theme.app.hover },
  },
  rowOff: { opacity: 0.5, cursor: "default", "&:hover": { background: "transparent" } },
  num: { fontFamily: theme.app.mono, fontSize: 14, color: theme.app.ink, fontWeight: 500 },
  meta: { fontSize: 12, color: theme.app.textTertiary },
  tag: { marginLeft: "auto", fontSize: 11, color: theme.app.textTertiary },
  empty: { padding: "26px 10px", textAlign: "center", color: theme.app.textTertiary, fontSize: 13.5, lineHeight: 1.5 },
  center: { display: "flex", justifyContent: "center", padding: 28 },
  foot: { display: "flex", alignItems: "center", gap: 10, padding: "12px 20px 18px" },
  count: { fontSize: 13, color: theme.app.textSecondary },
  grow: { flex: 1 },
}));

const ImportBrdidModal = ({ open, onClose, onImport }) => {
  const classes = useStyles();
  const [filter, setFilter] = useState("");
  const [dids, setDids] = useState([]);
  const [total, setTotal] = useState(0);
  const [available, setAvailable] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState({}); // number -> bool

  const load = useCallback(
    async f => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get("/numbers/brdid/available", { params: { filter: f || undefined } });
        setDids(data.dids || []);
        setTotal(data.total || 0);
        setAvailable(data.available || 0);
      } catch (e) {
        setDids([]);
        setTotal(0);
        setError((e.response && e.response.data && e.response.data.error) || "Não foi possível buscar os números.");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (open) {
      setFilter("");
      setSelected({});
      load("");
    }
  }, [open, load]);

  // busca ao digitar (debounce simples)
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => load(filter), 350);
    return () => clearTimeout(t);
  }, [filter, open, load]);

  const toggle = d => {
    if (d.alreadyImported) return;
    setSelected(s => ({ ...s, [d.number]: !s[d.number] }));
  };
  const selectedNumbers = Object.keys(selected).filter(n => selected[n]);

  // a importação em si roda no pai (em segundo plano, com o modal de progresso)
  const doImport = () => {
    if (!selectedNumbers.length) return;
    onClose();
    if (onImport) onImport(selectedNumbers);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} PaperProps={{ className: classes.paper }}>
      <div className={classes.head}>
        <div className={classes.titleRow}>
          <img src={brdidLogo} alt="BR DID" className={classes.brdLogo} />
          <span className={classes.title}>Importar números</span>
        </div>
        <div className={classes.sub}>Selecione os números que você tem na BR DID para trazer para sua conta.</div>
      </div>

      <div className={classes.body}>
        <TextField
          className={classes.search}
          placeholder="Buscar por número ou descrição…"
          variant="outlined"
          size="small"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {loading ? (
          <div className={classes.center}>
            <CircularProgress size={26} />
          </div>
        ) : error ? (
          <div className={classes.empty}>{error}</div>
        ) : dids.length === 0 ? (
          <div className={classes.empty}>Nenhum número disponível na BR DID{filter ? " com esse filtro" : ""}.</div>
        ) : (
          dids.map(d => (
            <div
              key={d.number}
              className={`${classes.row} ${d.alreadyImported ? classes.rowOff : ""}`}
              onClick={() => toggle(d)}
            >
              <Checkbox
                size="small"
                checked={!!selected[d.number] || d.alreadyImported}
                disabled={d.alreadyImported}
                color="primary"
              />
              <div>
                <div className={classes.num}>{fmt(d.number)}</div>
                {(d.city || d.label) && <div className={classes.meta}>{[d.label, d.city].filter(Boolean).join(" · ")}</div>}
              </div>
              {d.alreadyImported && <span className={classes.tag}>já na conta</span>}
            </div>
          ))
        )}
      </div>

      <div className={classes.foot}>
        <span className={classes.count}>
          {selectedNumbers.length
            ? `${selectedNumbers.length} selecionado(s)`
            : filter
            ? total > dids.length
              ? `${dids.length} de ${total} — refine a busca`
              : `${total} encontrado(s)`
            : `${dids.length} de ${available} — busque para ver mais`}
        </span>
        <div className={classes.grow} />
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!selectedNumbers.length}
          onClick={doImport}
        >
          Importar selecionados
        </Button>
      </div>
    </Dialog>
  );
};

export default ImportBrdidModal;
